import {
  Button,
  Flex,
  Text,
  Circle,
  Panel,
  Loader,
  Select,
} from "@coaching-culture/ui";
import styled from "styled-components";
import { lighten } from "polished";
import { FaFileUpload } from "react-icons/fa";
import Papa from "papaparse";
import jschardet from "jschardet";
import { useMemo, useState } from "react";
import CenterColumn from "components/CenterColumn";
import { PageHeader } from "components/PageHeader";
import { useOrgUsers } from "queries/users";
import axios from "axios";
import { useUserFields } from "@coaching-culture/api-client";
import { UserFieldType } from "@coaching-culture/types";

const UploadBox = styled(Flex)`
  border: 3px dashed ${(props) => props.theme.colors.primary};
  border-radius: 6px;
  width: 500px;
  margin-left: auto;
  margin-right: auto;
  background-color: ${(props) => lighten(0.44, props.theme.colors.primary)};
`;

const FileButton = styled.label`
  border: 1px solid ${(props) => props.theme.colors.primary};
  background-color: transparent;
  color: ${(props) => props.theme.colors.primary};
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 24px;
  display: inline-block;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
    color: white;
  }

  & input {
    display: none;
  }
`;

type CsvLine = Record<string, string>;
type DeleteResult = { deletedUsers: string[]; notFoundUser: string[] };

export const BulkDelete = () => {
  const [data, setData] = useState<CsvLine[] | null>(null);
  const [deleteResult, setDeleteResult] = useState<DeleteResult>({
    deletedUsers: [],
    notFoundUser: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [field, setField] = useState("email");
  const userFields = useUserFields();

  const { data: users, isFetched } = useOrgUsers();

  const usersToDelete = useMemo(() => {
    if (users == null || data == null) {
      return null;
    }

    const d = new Set(data.map((x) => x[0].trim().toLowerCase()));

    if (field === "email") {
      return users.filter((x) => d.has(x.email));
    } else if (field.startsWith("uf-")) {
      const id = field.substring(3);
      return users.filter((x) =>
        d.has(x.userFields.find((uf) => uf.id === id)?.value as string),
      );
    } else {
      return null;
    }
  }, [field, data, users]);

  const handleFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files[0];
    if (file == null) {
      return window.alert("Unable to read file");
    }

    const reader = new FileReader();

    reader.onload = function (e) {
      const csvResult = e.target.result as string;
      const enc = jschardet.detect(csvResult).encoding;
      Papa.parse<CsvLine>(file, {
        delimiter: " ",
        encoding: enc,
        skipEmptyLines: true,
        complete: (data) => {
          if (data.errors.length > 0) {
            ev.target.value = "";
            window.alert(
              "There was an error reading the CSV:" + data.errors[0].message,
            );
          } else {
            if (!Array.isArray(data.data)) {
              window.alert("Unable to read CSV");
              return;
            }
            if (data.data.length === 0) {
              window.alert("Empty CSV found");
              return;
            }
            setData(data.data);
          }
        },
      });
    };
    reader.readAsBinaryString(file);
  };

  const deleteUsers = () => {
    setLoading(true);

    console.log(usersToDelete);

    setDeleteResult({
      deletedUsers: usersToDelete.map((x) => x.email),
      notFoundUser: [],
    });

    Promise.all(
      usersToDelete.map((x) => axios.delete(`/api/users/${x.id}`)),
    ).then((res) => {
      setLoading(false);
      setShowResult(true);
    });
  };

  const fieldOptions = [
    { value: "email", label: "Email" },
    ...(userFields.data ?? [])
      .filter((x) => x.type === UserFieldType.FreeText)
      .map((x) => ({
        value: `uf-${x.id!}`,
        label: `User Field: ${x.name}`,
      })),
  ];

  return (
    <CenterColumn>
      <PageHeader
        text="Bulk User Delete"
        subtitle="Upload a CSV of a list of emails to delete a large number of users quickly"
        backUrl="/success/people/users"
      />
      <Panel p={3}>
        {loading || !isFetched ? (
          <Loader />
        ) : showResult ? (
          <>
            <Text fontWeight={600} fontSize={4}>
              Deleted users:
            </Text>
            <ul>
              {deleteResult.deletedUsers.map((x) => (
                <li>{x}</li>
              ))}
            </ul>
            <Text fontWeight={600} fontSize={4}>
              Users not found:
            </Text>
            <ul>
              {deleteResult.notFoundUser.map((x) => (
                <li>{x}</li>
              ))}
            </ul>
            <Flex mt={2} alignItems={"flex-start"}>
              <Button to="/success/people/users">Return to Users</Button>
            </Flex>
          </>
        ) : data === null ? (
          <>
            <Flex justifyContent={"center"} alignItems={"center"} mb={3}>
              <Text fontWeight={600} style={{ whiteSpace: "nowrap" }} mr={2}>
                Delete By:
              </Text>
              <Select
                options={fieldOptions}
                value={field}
                onChange={(ev) => setField(ev.target.value)}
              />
            </Flex>
            <UploadBox
              justifyContent="center"
              alignItems="center"
              p={5}
              flexDirection="column"
            >
              <Circle
                icon={FaFileUpload}
                size="xlarge"
                color="primary"
                mb={3}
              />
              <Text fontWeight={600} fontSize={4} textAlign="center">
                Upload CSV
              </Text>
              <Text mb={4} width={300} textAlign="center">
                Ensure there is only one
                <br /> column with <strong>no</strong> header with
                <br /> one user identifier per line
              </Text>
              <Flex alignItems="center">
                <FileButton as="label">
                  Select CSV File
                  <input type="file" onChange={handleFileChange} />
                </FileButton>
              </Flex>
            </UploadBox>
          </>
        ) : (
          <>
            <Text fontWeight={600} fontSize={4}>
              Users to delete
            </Text>
            <ul>
              {usersToDelete.map((x) => (
                <li>{x.email}</li>
              ))}
            </ul>
            <Flex alignItems={"flex-start"}>
              <Button color="danger" onClick={deleteUsers}>
                Delete Users
              </Button>
            </Flex>
          </>
        )}
      </Panel>
    </CenterColumn>
  );
};
